import { CTA_CLICKED_VALUES, STATSIG_EVENTS } from "@repo/statsig";
import { useStatsigClient } from "@repo/statsig";
import BrandedActionButton from "src/components/branded-action-button";
import {
  BodyText,
  EscalatorText,
  HeadlineText,
  SublineCondensedText,
} from "src/components/KinsaTypography";
import type { AvatarProps } from "@repo/ui/avatar";
import Avatar from "@repo/ui/avatar";
import LockIcon from "./lock-icon";
import PaymentProcessingIssueState from "./states/payment-processing-issue";
import SubscriptionCancelledState from "./states/subscription-cancelled";

export type EmptyStateVariant =
  | "active"
  | "never-subscribed"
  | "cancelled"
  | "payment_issue";

export interface AvatarEntry {
  variant: AvatarProps["variant"];
  id: string;
}

interface KinsaVisitListEmptyPureProps {
  effectiveState: EmptyStateVariant | undefined;
  avatars: AvatarEntry[];
  onGetCareNow: () => void;
  onPaywall: () => void;
  onValueProps: () => void;
  isPending: boolean;
  patientCount: number;
}

const ONBOARDING_STEPS: readonly {
  title: string;
  subtitle: string;
  color: string;
  active?: boolean;
}[] = [
  {
    title: "Choose your plan",
    subtitle: "And start your 14-day free trial",
    color: "#00A1C5",
    active: true,
  },
  {
    title: "Your first visit",
    subtitle: "Send symptoms for review & get treatment fast",
    color: "#A841BA",
  },
  {
    title: "Health profile built",
    subtitle: "Save allergies, medications, health history",
    color: "#10B981",
  },
  {
    title: "Family members added",
    subtitle: "Care for the whole family included in one plan",
    color: "#00A1C5",
  },
  {
    title: "Follow-up care",
    subtitle: "Ongoing support when you need it",
    color: "#F59E0B",
  },
  {
    title: "Smart insights activated",
    subtitle: "Personalized alerts for your family",
    color: "#6366F1",
  },
];

const ChevronRightIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.25 3.5L8.75 7L5.25 10.5"
      stroke="#00A1C5"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function AvatarIllustration({
  avatars,
  hasHalo,
}: {
  avatars: AvatarEntry[];
  hasHalo?: boolean;
}) {
  const avatarContent =
    avatars.length > 0 ? (
      avatars.map((avatar, index) => (
        <Avatar
          key={avatar.id}
          variant={avatar.variant}
          size="medium"
          className={index < avatars.length - 1 ? "mr-[-16px]" : ""}
        />
      ))
    ) : (
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-teal-700 border-t-transparent" />
    );

  if (!hasHalo) {
    return (
      <div className="mb-6 flex items-center justify-center">
        {avatarContent}
      </div>
    );
  }

  return (
    <div
      className="mb-6 flex items-center justify-center rounded-[999px] px-12 py-8"
      style={{ backgroundColor: "rgba(0,161,197,0.05)" }}
    >
      <div
        className="flex items-center justify-center rounded-[999px] px-6 py-6"
        style={{ backgroundColor: "rgba(0,161,197,0.09)" }}
      >
        {avatarContent}
      </div>
    </div>
  );
}

const KinsaVisitListEmptyPure: React.FC<KinsaVisitListEmptyPureProps> = ({
  effectiveState,
  avatars,
  onGetCareNow,
  onPaywall,
  onValueProps,
  isPending,
  patientCount,
}) => {
  const { logEvent } = useStatsigClient();

  const handleChoosePlanClick = () => {
    logEvent({
      eventName: "cta_clicked",
      value: "choose_your_plan",
      metadata: {
        action: "choose_your_plan",
        source: "never_subscribed_empty_state",
        patient_count: patientCount.toString(),
      },
    });
    onPaywall();
  };

  if (effectiveState === "cancelled") {
    return <SubscriptionCancelledState />;
  }

  if (effectiveState === "payment_issue") {
    return <PaymentProcessingIssueState />;
  }

  if (effectiveState === "never-subscribed") {
    // -mb-[102px] counteracts ListPageLayout's pb-[260px] scroll padding (minus 158px safe zone),
    // which exists for the gradient fade on scrollable visit lists but
    // creates excessive blank space after this self-contained empty state.
    return (
      <div className="flex flex-col items-center px-4 pt-12 -mb-[102px] pb-[var(--kinsa-tab-bar-offset,0px)] text-center">
        <AvatarIllustration avatars={avatars} hasHalo />

        <HeadlineText className="mb-1 text-text">
          Care for the whole family
        </HeadlineText>
        <BodyText className="mb-8 max-w-sm text-text-muted">
          Board-certified doctors available 24/7.
          <br />
          The fastest way to get treatment.
        </BodyText>

        <div className="flex flex-col gap-2 w-full">
          <BrandedActionButton
            text="GET CARE NOW"
            onPress={onGetCareNow}
            eventData={{
              action: CTA_CLICKED_VALUES.START_VISIT,
              source: "never_subscribed_empty_state",
              patient_count: patientCount.toString(),
              next_screen: patientCount > 1 ? "patient_selector" : "intake",
            }}
            className="w-full"
            disabled={isPending}
            loading={isPending}
          />
          <BrandedActionButton
            text="EXPLORE PLANS & LEARN MORE"
            onPress={onValueProps}
            eventData={{
              action: "explore_plans",
              source: "never_subscribed_empty_state",
              patient_count: patientCount.toString(),
            }}
            variant="tertiary"
            className="w-full"
          />
        </div>

        {/* Divider */}
        <div className="my-6 h-px w-full bg-[#F0F0F0]" />

        {/* Onboarding timeline */}
        <div className="w-full">
          {ONBOARDING_STEPS.map((step, index) => (
            <div key={step.title} className="flex gap-3">
              {/* Left indicator column — relative so line can extend past row boundary */}
              <div className="relative w-6 shrink-0 self-stretch">
                {/* Circle indicator — positioned to align with card content */}
                <div
                  className="absolute left-0 top-[12px] flex h-6 w-6 items-center justify-center rounded-full border-2"
                  style={{
                    borderColor: step.color,
                    backgroundColor: step.active ? `${step.color}15` : "white",
                  }}
                >
                  {step.active ? (
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: step.color }}
                    />
                  ) : (
                    <LockIcon size={12} color={step.color} />
                  )}
                </div>
                {/* Connector line — extends from below circle through the gap to the next circle */}
                {index < ONBOARDING_STEPS.length - 1 && (
                  <div
                    className="absolute left-[11px] w-0.5"
                    style={{
                      backgroundColor: step.color,
                      top: "36px",
                      bottom: "-26px",
                    }}
                  />
                )}
              </div>

              {/* Step card */}
              <div
                className={`mb-[14px] flex w-full items-center justify-between rounded-xl border px-4 py-[15px] ${step.active ? "cursor-pointer" : ""}`}
                style={
                  step.active
                    ? {
                        backgroundColor: "rgba(0,161,197,0.03)",
                        borderColor: "rgba(0,161,197,0.19)",
                      }
                    : {
                        backgroundColor: "#F8FAFB",
                        borderColor: "rgba(0,0,0,0.04)",
                      }
                }
                {...(step.active && {
                  onClick: handleChoosePlanClick,
                  role: "button",
                  tabIndex: 0,
                })}
              >
                <div className="text-left text-text-muted">
                  <EscalatorText className="block text-text">
                    {step.title}
                  </EscalatorText>
                  <SublineCondensedText>{step.subtitle}</SublineCondensedText>
                </div>
                {step.active ? (
                  <ChevronRightIcon />
                ) : (
                  <LockIcon size={16} color="#9AA4B2" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Active subscription empty state (default)
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <AvatarIllustration avatars={avatars} hasHalo />

      <HeadlineText className="mb-3 text-text">
        How can we help you today?
      </HeadlineText>
      <BodyText className="mb-8 max-w-sm text-text-muted leading-relaxed">
        Our board-certified doctors are available 24/7, for issues big or small.
      </BodyText>

      <BrandedActionButton
        text="GET CARE NOW"
        onPress={onGetCareNow}
        eventData={{
          action: CTA_CLICKED_VALUES.START_VISIT,
          patient_count: patientCount?.toString() || "0",
          has_existing_visits: "false",
        }}
        customEventName={STATSIG_EVENTS.START_A_VISIT}
        className="w-full max-w-sm"
        disabled={isPending}
        loading={isPending}
      />
    </div>
  );
};

export default KinsaVisitListEmptyPure;
